import { useState, useEffect } from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import CanonicalTag from "@/components/CanonicalTag";
import { NeumorphicThemeProvider, useNeumorphicTheme } from "@/contexts/NeumorphicThemeContext";
import NeumorphicThemeToggle from "@/components/NeumorphicThemeToggle";
import { NeumorphicContainer, NeumorphicCard } from "@/components/neumorphic";

const FAQContent = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const { theme } = useNeumorphicTheme();

  const openCalendlyModal = () => {
    setShowCalendly(true);
  };

  const closeCalendlyModal = () => {
    setShowCalendly(false);
  };

  const openCalendly = () => {
    window.open('https://calendly.com/udaadu', '_blank');
  };

  useEffect(() => {
    // Add extended FAQPage schema for FAQ page
    const faqSchema = document.createElement('script');
    faqSchema.type = 'application/ld+json';
    faqSchema.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How much does it cost to build an ADU?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "UDA ADU projects typically range from $150,000 and UP depending on size, layout, and site conditions. Use our ADU Calculator for a free estimate."
          }
        },
        {
          "@type": "Question",
          "name": "Can I rent out my ADU for income?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes — most UDA ADUs earn between $2,500 and $7,000 per month in rental income depending on location and size."
          }
        },
        {
          "@type": "Question",
          "name": "Do you offer custom ADUs or only pre-approved plans?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We offer both: fully custom ADU designs tailored to your lot, and pre-approved plans for faster builds with guaranteed zoning compliance."
          }
        },
        {
          "@type": "Question",
          "name": "How long does it take to design and build an ADU?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most of our ADU projects take 4–6 months from site analysis to move-in. Our pre-permitted plans can reduce timelines significantly."
          }
        },
        {
          "@type": "Question",
          "name": "Do I need a permit for a garage conversion?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, any garage-to-ADU conversion requires city approval. UDA ADU manages the entire permitting process for you."
          }
        },
        {
          "@type": "Question",
          "name": "What cities do you serve?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We specialize in ADUs throughout the Bay Area, including San Jose, Sunnyvale, Los Altos, Palo Alto, Santa Clara, and surrounding cities."
          }
        }
      ]
    });
    document.head.appendChild(faqSchema);

    // Cleanup function
    return () => {
      const existingFaqSchema = document.querySelector('script[type="application/ld+json"]:last-of-type');
      if (existingFaqSchema && existingFaqSchema.innerHTML.includes('FAQPage')) {
        existingFaqSchema.remove();
      }
    };
  }, []);

  const textColor = theme === "dark" ? "text-white" : "text-[#1a1a1a]";
  const mutedTextColor = theme === "dark" ? "text-gray-300" : "text-[#4a4a4a]";
  const sectionHeaderColor = theme === "dark" ? "text-white" : "text-[#1a1a1a]";

  const cardStyles = theme === "dark"
    ? "bg-[#0a0a0a] rounded-2xl p-6 shadow-[8px_8px_16px_#000000,-8px_-8px_16px_#141414] transition-all duration-500"
    : "bg-[#f5f5f5] rounded-2xl p-6 shadow-[8px_8px_16px_#d8d8d8,-8px_-8px_16px_#fefefe] transition-all duration-500";

  return (
    <NeumorphicContainer>
      <CanonicalTag url="https://www.udaadu.com/faq" />
      <Navigation />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <NeumorphicCard className="relative w-full max-w-4xl h-[90vh] overflow-hidden p-2">
            <button 
              onClick={closeCalendlyModal}
              className={`absolute top-4 right-4 z-10 p-2 rounded-full shadow-md transition-colors ${
                theme === "dark" 
                  ? "bg-[#0a0a0a] hover:bg-[#141414] text-white" 
                  : "bg-[#f5f5f5] hover:bg-[#e8e8e8] text-[#1a1a1a]"
              }`}
            >
              <X className="h-6 w-6" />
            </button>
            <iframe
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1"
              width="100%"
              height="100%"
              frameBorder="0"
              title="Schedule a meeting"
              className="rounded-xl"
            />
          </NeumorphicCard>
        </div>
      )}

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl mb-4 text-[#e4683b] font-light md:text-3xl">
            Frequently Asked Questions
          </h1>
          <p className={`text-lg ${mutedTextColor}`}>
            Everything you need to know about ADUs and our services
          </p>
        </div>

        {/* General ADU Questions */}
        <section className="mb-12">
          <h2 className={`text-2xl font-bold mb-6 border-b-2 border-[#e4683b] pb-2 ${sectionHeaderColor}`}>
            General ADU Questions
          </h2>
          
          <div className="space-y-6">
            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                1. What is an ADU (Accessory Dwelling Unit)?
              </h3>
              <p className={mutedTextColor}>
                An ADU is an additional housing unit on your property—like a backyard cottage or apartment over a garage—that can serve as a rental, guest suite, or in-law unit.
              </p>
            </div>

            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                2. Why should I build an ADU?
              </h3>
              <p className={mutedTextColor}>
                Building an ADU increases overall property value, generates rental income, helps family housing needs, and provides a flexible, cost-effective living space.
              </p>
            </div>

            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 flex items-center gap-2 ${textColor}`}>
                3. How do I get started with UDA ADU
                <Button 
                  onClick={openCalendlyModal}
                  className="bg-[#e4683b] hover:bg-[#d55a31] text-white text-xs px-2 py-1 h-auto"
                  size="sm"
                >
                  Book Now
                </Button>
                ?
              </h3>
              <p className={mutedTextColor}>
                Your journey begins with a free, no-obligation feasibility meeting. We assess your property, zoning eligibility, cost estimates, and financier options.
              </p>
            </div>
          </div>
        </section>

        {/* Process & Planning */}
        <section className="mb-12">
          <h2 className={`text-2xl font-bold mb-6 border-b-2 border-[#e4683b] pb-2 ${sectionHeaderColor}`}>
            Process & Planning
          </h2>
          
          <div className="space-y-6">
            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                4. What happens during the free feasibility meeting?
              </h3>
              <p className={mutedTextColor}>
                You'll receive a detailed report: ADU feasibility, estimated return on investment, home value gain, financing/loan options, transparent pricing, and next steps.
              </p>
            </div>

            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                5. What's the timeline and process flow?
              </h3>
              <div className={mutedTextColor}>
                <p>Schedule free consult →</p>
                <p>Receive feasibility + pricing →</p>
                <p>Design + permitting →</p>
                <p>Construction →</p>
                <p>Final inspection and move-in</p>
              </div>
            </div>
          </div>
        </section>

        {/* Costs, Financing & ROI */}
        <section className="mb-12">
          <h2 className={`text-2xl font-bold mb-6 border-b-2 border-[#e4683b] pb-2 ${sectionHeaderColor}`}>
            Costs, Financing & ROI
          </h2>
          
          <div className="space-y-6">
            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                6. How much does an ADU cost to build?
              </h3>
              <p className={mutedTextColor}>
                Costs vary based on size, type, location, and finishes. Our intuitive ADU Calculator provides accurate pricing, projected ROI, and home value gain tailored to your address.
              </p>
            </div>

            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                7. What financing options are available?
              </h3>
              <p className={mutedTextColor}>
                We cover all major options: construction loans, HELOCs, cash-out refi, grants, and equity-based loans. We'll walk you through what's best for your situation.
              </p>
            </div>

            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                8. Will I make money on this investment?
              </h3>
              <p className={mutedTextColor}>
                Yes. We illustrate ROI projections, estimate rental income, and compare those to your building costs—showing long-term financial benefits.
              </p>
            </div>
          </div>
        </section>

        {/* ADU Calculator */}
        <section className="mb-12">
          <h2 className={`text-2xl font-bold mb-6 border-b-2 border-[#e4683b] pb-2 ${sectionHeaderColor}`}>
            ADU Calculator
          </h2>
          
          <div className="space-y-6">
            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                9. What is the ADU Calculator?
              </h3>
              <p className={mutedTextColor}>
                It's a proprietary tool built by UDA ADU that calculates transparent pricing, financing cost, home value increase, rental ROI, and more—based on your specific address and unit specs.
              </p>
            </div>

            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                10. How accurate is the ADU Calculator?
              </h3>
              <p className={mutedTextColor}>
                Extremely. We use verified local construction costs, market data, permit fees, and your inputs (size, finishes, loan details) to provide precise estimates.
              </p>
            </div>

            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                11. Is there a cost to use it?
              </h3>
              <p className={mutedTextColor}>
                Nope! The calculator is completely free—no sign-up or obligation required.
              </p>
            </div>
          </div>
        </section>

        {/* Zoning, Regulations & Local Info */}
        <section className="mb-12">
          <h2 className={`text-2xl font-bold mb-6 border-b-2 border-[#e4683b] pb-2 ${sectionHeaderColor}`}>
            Zoning, Regulations & Local Info
          </h2>
          
          <div className="space-y-6">
            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                12. What are local zoning requirements for ADUs?
              </h3>
              <p className={mutedTextColor}>
                Zoning rules vary by location. Our ADU Insider page includes detailed info on local regulations, setbacks, square footage limits, and permitting requirements.
              </p>
            </div>

            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                13. How do you handle permitting and approvals?
              </h3>
              <p className={mutedTextColor}>
                We fully manage the permitting process—from design submission and reviews to final approvals—so you don't have to worry about red tape.
              </p>
            </div>
          </div>
        </section>

        {/* After Construction */}
        <section className="mb-12">
          <h2 className={`text-2xl font-bold mb-6 border-b-2 border-[#e4683b] pb-2 ${sectionHeaderColor}`}>
            After Construction
          </h2>
          
          <div className="space-y-6">
            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                14. Can the ADU be rented out legally?
              </h3>
              <p className={mutedTextColor}>
                Yes, most ADUs are legal rentals under state and city laws. We help you get proper permitting and guide you through landlord requirements.
              </p>
            </div>

            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                15. What about maintenance and long-term management?
              </h3>
              <p className={mutedTextColor}>
                We provide ongoing support options, including maintenance coordination, rental management strategies, and property management referrals.
              </p>
            </div>
          </div>
        </section>

        {/* Miscellaneous */}
        <section className="mb-12">
          <h2 className={`text-2xl font-bold mb-6 border-b-2 border-[#e4683b] pb-2 ${sectionHeaderColor}`}>
            Miscellaneous
          </h2>
          
          <div className="space-y-6">
            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                16. How long does it take?
              </h3>
              <p className={mutedTextColor}>
                From design to move-in, typical ADU projects take 9–12 months, depending on permits and construction timelines.
              </p>
            </div>

            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                17. What makes UDA ADU different?
              </h3>
              <p className={mutedTextColor}>
                We offer unmatched transparency—with clear cost, ROI data, local regulation guidance, easy-to-use tools, and full-service project management from start to finish.
              </p>
            </div>

            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                18. What if I only need some services?
              </h3>
              <p className={mutedTextColor}>
                No problem! You can engage us for design-only, permit-only, or full build—with custom scopes to fit your needs.
              </p>
            </div>
          </div>
        </section>

        {/* Selling Your ADU Separately */}
        <section className="mb-12">
          <h2 className={`text-2xl font-bold mb-6 border-b-2 border-[#e4683b] pb-2 ${sectionHeaderColor}`}>
            Selling Your ADU Separately
          </h2>
          
          <div className="space-y-6">
            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                19. Can I sell my ADU as a separate property?
              </h3>
              <p className={mutedTextColor}>
                Yes! Under AB 1033, California homeowners may sell their ADU as a standalone unit—similar to a condominium—if their city has passed a local ordinance allowing it.
              </p>
            </div>

            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                20. When did this new law become effective?
              </h3>
              <p className={mutedTextColor}>
                AB 1033 was signed in October 2023 and came into full effect on January 1, 2024.
              </p>
            </div>

            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                21. Does this apply statewide immediately?
              </h3>
              <p className={mutedTextColor}>
                No—while the state law permits separate ADU sales, local jurisdictions must opt-in. For example, San Jose approved its ordinance in mid-2024, but other cities may still be evaluating the change.
              </p>
            </div>

            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                22. Which California cities currently allow ADU sales?
              </h3>
              <p className={mutedTextColor}>
                As of mid-2024, cities like San Jose, Oakland, and Sacramento have adopted supportive ordinances. Others, including San Francisco, are still under review.
              </p>
            </div>

            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                23. What is the process to sell an ADU separately?
              </h3>
              <p className={mutedTextColor}>
                You would subdivide your property into a condo-like arrangement with a homeowners association, allowing the ADU to be sold independently from the main home.
              </p>
            </div>

            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                24. Why does UDA ADU support this flexibility?
              </h3>
              <p className={mutedTextColor}>
                We believe in offering all paths to homeownership and wealth-building. Whether you rent, keep, or sell your ADU, we're here to help you make the best choice for your goals.
              </p>
            </div>
          </div>
        </section>

        {/* Junior ADUs */}
        <section className="mb-12">
          <h2 className={`text-2xl font-bold mb-6 border-b-2 border-[#e4683b] pb-2 ${sectionHeaderColor}`}>
            Junior ADUs (JADUs)
          </h2>
          
          <div className="space-y-6">
            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                25. What is a Junior ADU (JADU)?
              </h3>
              <p className={mutedTextColor}>
                A JADU is a smaller living unit (up to 500 sq ft) built within an existing single-family home—often by converting a bedroom or garage.
              </p>
            </div>

            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                26. How is a JADU different from a regular ADU?
              </h3>
              <p className={mutedTextColor}>
                JADUs are smaller, must be part of the main home, and allow shared bathroom access. ADUs are fully independent units with their own entrance, kitchen, and bathroom.
              </p>
            </div>

            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                27. Do JADUs require a permit?
              </h3>
              <p className={mutedTextColor}>
                Yes—JADUs require city permits. However, the process is often simpler and faster than a full ADU since there's no new foundation or major construction.
              </p>
            </div>

            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                28. Can I have both an ADU and a JADU on my property?
              </h3>
              <p className={mutedTextColor}>
                Yes! California law allows one JADU and one ADU on qualifying single-family lots—doubling your passive income potential.
              </p>
            </div>
          </div>
        </section>

        {/* Pre-Approved & Pre-Designed Plans */}
        <section className="mb-12">
          <h2 className={`text-2xl font-bold mb-6 border-b-2 border-[#e4683b] pb-2 ${sectionHeaderColor}`}>
            Pre-Approved & Pre-Designed Plans
          </h2>
          
          <div className="space-y-6">
            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                29. What is a pre-approved ADU plan?
              </h3>
              <p className={mutedTextColor}>
                A pre-approved plan is already vetted by a city's planning department. It meets all local codes, which speeds up your permit timeline and lowers project risk.
              </p>
            </div>

            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                30. What is a pre-designed ADU plan?
              </h3>
              <p className={mutedTextColor}>
                A pre-designed plan is a ready-made layout created by UDA ADU that hasn't yet gone through city approval—but can be adapted and submitted when you're ready.
              </p>
            </div>

            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                31. Why choose a pre-approved plan?
              </h3>
              <p className={mutedTextColor}>
                It saves weeks of permit processing time, reduces design costs, and gives you peace of mind that your ADU meets all requirements from day one.
              </p>
            </div>

            <div className={cardStyles}>
              <h3 className={`text-lg font-semibold mb-3 ${textColor}`}>
                32. Can I customize a pre-approved plan?
              </h3>
              <p className={mutedTextColor}>
                Yes—minor changes are often allowed. Major layout changes may require resubmission for approval. We'll walk you through options during your feasibility meeting.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <NeumorphicCard className="p-8">
            <h2 className={`text-2xl font-bold mb-4 ${textColor}`}>Ready to Get Started?</h2>
            <p className={`mb-6 ${mutedTextColor}`}>
              Schedule your free consultation today and let's discuss your ADU project.
            </p>
            <Button 
              onClick={openCalendlyModal}
              className="bg-[#e4683b] hover:bg-[#d55a31] text-white px-8 py-3 text-lg"
            >
              Book Your Free Consultation
            </Button>
          </NeumorphicCard>
        </div>
      </main>

      <Footer />
      <FloatingADUWizard />
      <NeumorphicThemeToggle />
    </NeumorphicContainer>
  );
};

const FAQ = () => {
  return (
    <NeumorphicThemeProvider>
      <FAQContent />
    </NeumorphicThemeProvider>
  );
};

export default FAQ;
